import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { 
  Lightning, ChartBar, Users, CurrencyDollar, FileText, Buildings,
  SignOut, ArrowLeft, Crown, FilePdf, EnvelopeSimple, LinkSimple, ClipboardText,
} from "@phosphor-icons/react";
import { Toaster } from "sonner";

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const navItems = [
    { path: "/admin", icon: ChartBar, label: "Dashboard", exact: true },
    { path: "/admin/users", icon: Users, label: "Users" },
    { path: "/admin/grants", icon: CurrencyDollar, label: "Grants" },
    { path: "/admin/applications", icon: FileText, label: "Applications" },
    { path: "/admin/corporate-leads", icon: Buildings, label: "Corporate Leads" },
    { path: "/admin/newsletter", icon: EnvelopeSimple, label: "Newsletter" },
    { path: "/admin/retainer-links", icon: LinkSimple, label: "Retainer Links" },
    { path: "/admin/fcs-intakes", icon: ClipboardText, label: "FCS Intakes" },
    { path: "/admin/documents", icon: FilePdf, label: "Documents" },
    { path: "/admin/impact-emails", icon: FilePdf, label: "Impact Emails" },
  ];

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-black flex flex-col lg:flex-row">
      <Toaster position="top-right" theme="dark" />

      {/* Mobile header (hidden on desktop) */}
      <header className="lg:hidden flex items-center justify-between px-4 py-3 bg-zinc-950 border-b border-zinc-800" data-testid="admin-mobile-header">
        <Link to="/admin" className="flex items-center gap-2">
          <Lightning weight="fill" className="w-6 h-6 text-emerald-500" />
          <span className="text-base font-bold tracking-tight">FlashGrants</span>
          <span className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-yellow-500/10 border border-yellow-500/30">
            <Crown weight="fill" className="w-3 h-3 text-yellow-500" />
            <span className="text-[9px] text-yellow-500 uppercase tracking-widest font-semibold">Admin</span>
          </span>
        </Link>
        <div className="flex items-center gap-2">
          <Link to="/dashboard" className="p-2 rounded-sm text-zinc-500 hover:text-white" title="Back to App" data-testid="admin-mobile-back">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <button onClick={handleLogout} className="p-2 rounded-sm text-zinc-500 hover:text-white" title="Sign Out" data-testid="admin-mobile-logout">
            <SignOut className="w-5 h-5" />
          </button>
        </div>
      </header>

      {/* Mobile nav strip — horizontally scrollable (hidden on desktop) */}
      <nav className="lg:hidden flex gap-1 overflow-x-auto px-3 py-2 bg-zinc-950 border-b border-zinc-800 scrollbar-thin" data-testid="admin-mobile-nav">
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className={`flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold whitespace-nowrap transition-colors ${
              isActive(item.path, item.exact)
                ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
                : "bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-zinc-200"
            }`}
          >
            <item.icon className="w-3.5 h-3.5" />
            {item.label}
          </Link>
        ))}
      </nav>

      {/* Desktop sidebar (hidden on mobile) */}
      <aside className="hidden lg:flex w-64 bg-zinc-950 border-r border-zinc-800 flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-zinc-800">
          <Link to="/admin" className="flex items-center gap-2">
            <Lightning weight="fill" className="w-7 h-7 text-emerald-500" />
            <span className="text-lg font-bold tracking-tight">FlashGrants</span>
          </Link>
          <div className="flex items-center gap-2 mt-2">
            <Crown weight="fill" className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-yellow-500 uppercase tracking-widest">Admin Panel</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          {navItems.map((item) => (
            <Link 
              key={item.path}
              to={item.path}
              className={`sidebar-link ${isActive(item.path, item.exact) ? 'active' : ''}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-zinc-800 space-y-2">
          <Link 
            to="/dashboard"
            className="sidebar-link text-zinc-500 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to App
          </Link>
          <button 
            onClick={handleLogout}
            className="sidebar-link text-zinc-500 hover:text-white w-full"
          >
            <SignOut className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-5 lg:py-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
